import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "../../utils/AxiosConfig";
import "../../Styles/userorder.css";
import { FiArrowLeft, FiCreditCard, FiXCircle } from "react-icons/fi";

const getReadableStatus = (value) =>
  String(value || "pending")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

function PaymentCancel() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentDetail, setPaymentDetail] = useState(null);

  const orderId = Number(searchParams.get("order_id") || 0);
  const returnProvider = searchParams.get("provider") || "";
  const isTamaraPayment =
    String(paymentDetail?.payment_method || paymentDetail?.latest_payment?.provider || returnProvider)
      .trim().toLowerCase() === "tamara";
  const isTabbyPayment =
    String(paymentDetail?.payment_method || paymentDetail?.latest_payment?.provider || returnProvider)
      .trim().toLowerCase() === "tabby";
  const bnplProvider = isTabbyPayment ? "Tabby" : "Tamara";
  const bnplPending = (isTamaraPayment || isTabbyPayment) &&
    String(paymentDetail?.payment_status || "pending").toLowerCase() === "pending";

  useEffect(() => {
    if (!orderId) return;

    const loadPayment = async () => {
      if (returnProvider === "tamara") {
        try {
          await axiosInstance.post(`tamara/orders/${orderId}/refresh-status`);
        } catch {
          // Continue with the last confirmed backend status.
        }
      }
      if (returnProvider === "tabby") {
        try {
          await axiosInstance.post(`tabby/orders/${orderId}/refresh-status`);
        } catch {
          // Continue with the last confirmed backend status.
        }
      }
      return axiosInstance.get(`user/orders/${orderId}/payment`);
    };

    loadPayment()
      .then((response) => {
        if (response.data?.success && response.data?.data) {
          setPaymentDetail(response.data.data);
          if (["tamara", "tabby"].includes(returnProvider) && response.data.data.payment_status === "paid") {
            navigate(`/payment/success?order_id=${orderId}&provider=${returnProvider}`, { replace: true });
          }
        }
      })
      .catch(() => {
        setPaymentDetail(null);
      });
  }, [orderId, returnProvider, navigate]);

  return (
    <div className="userorder-page">
      <div className="userorder-container">
        <section className="userorder-hero">
          <button
            className="userorder-back-btn"
            onClick={() => navigate("/cart")}
          >
            <FiArrowLeft /> Back to Cart
          </button>
          <div className="userorder-hero-copy">
            <h1>{bnplPending ? "Payment Processing" : "Payment Was Not Completed"}</h1>
            <p>{isTamaraPayment || isTabbyPayment
              ? `Your ${bnplProvider} checkout has not been confirmed as paid. Please check the order status before retrying.`
              : "Your Stripe checkout was cancelled or closed before payment confirmation finished."}</p>
          </div>
        </section>

        <section className="userorder-card userorder-summary-card">
          <div className="summary-copy">
            <div className="summary-title-row">
              <h2>
                {paymentDetail?.order_number
                  ? `Order ${paymentDetail.order_number}`
                  : orderId
                    ? `Order #${orderId}`
                    : "Order Payment"}
              </h2>
              <span
                className={`payment-status-chip ${String(
                  paymentDetail?.payment_status || "pending",
                )
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                {getReadableStatus(paymentDetail?.payment_status || "pending")}
              </span>
            </div>
            <p className="summary-subtitle">
              If you still want this order, you can reopen the order detail page
              and retry the {isTamaraPayment || isTabbyPayment ? bnplProvider : "Stripe"} payment from there.
            </p>
          </div>

          <div className="summary-actions">
            {orderId ? (
              <button
                className="userorder-primary-btn"
                onClick={() => navigate(`/userorder/${orderId}`)}
              >
                <FiCreditCard /> Go to Order
              </button>
            ) : null}
            {bnplPending ? (
              <button
                className="userorder-secondary-btn"
                onClick={() => navigate(`/payment/success?order_id=${orderId}&provider=${returnProvider}`)}
              >
                Check Payment Status
              </button>
            ) : null}
            <button
              className="userorder-secondary-btn"
              onClick={() => navigate("/cart")}
            >
              <FiXCircle /> Return to Cart
            </button>
          </div>
        </section>

        <section className="userorder-card">
          <div className="tracking-fallback">
            {isTamaraPayment || isTabbyPayment
              ? `A ${bnplProvider} redirect does not confirm payment. Your order status is taken from our server after checking ${bnplProvider}.`
              : "Stripe success is confirmed only by webhook processing. Because the payment was not completed here, this order remains unpaid until a successful Stripe event is received."}
          </div>
        </section>
      </div>
    </div>
  );
}

export default PaymentCancel;
